'use client';

import React from 'react';
import { GameInfoModalWrapper } from '../subcomponents/game-info-modal-wrapper';
import { getText } from '@/utils/text/text';
import { GameUrl } from '@/app/components/game/subcomponents/game-url/game-url';
import { useStore } from '@/store/store';
// eslint-disable-next-line nextjs-boundaries/no-server-import-in-client
import { SocialTagBadge } from '@/app/components/badge/layouts/social-tag/social-tag-badge';
import BadgeFactoryClient from '@/app/components/badge/badge-factory.client';
import { TextBadge } from '@/app/components/badge/layouts/text/text-badge';
import {
    deduplicateThemes,
    deduplicateFeatures,
    formatUid
} from '@/app/components/metadata-info-section/utils/metadata-helpers';
import { MetadataInfoSection } from '@/app/components/metadata-info-section/metadata-info-section';
import { toGameViewModel } from '@/logic/games/to-game-view-model';
import { POPUPS } from '@/app/components/popup/constants';
import type { Game as GameType } from '@/types/games';
import { FavoritesButton } from '@/app/elements/favorites-button/favorites-button';
import { FAVORITES_VARIANT } from '@/app/elements/favorites-button/favorites-button.constants';
import { useLauncherUrlResolution } from '@/hooks/use-launcher-url-resolution';
import { useGlobalContext } from '@/store/contexts/global-context';
import { SwjBorderOverlay } from '@/app/components/game/subcomponents/swj-border-overlay/swj-border-overlay';
import { getGameInfoModalMetadata } from '@/logic/game-adapter/game-adapter';

export const GameInfoModalAndroid: React.FC = () => {
    const { queue, closePopup } = useStore((s) => s.popup);
    const { region, isSiteWideJackpotEnabled } = useGlobalContext();
    // adr TODO asta o fost lasata la deruta?
    const launcherResolution = useLauncherUrlResolution();

    const gameInfoPopup = queue.find((item) => item.type === POPUPS.GAME_INFO);
    const isInQueue = !!gameInfoPopup;
    const gameData = gameInfoPopup?.params;

    if (!gameData) {
        return null;
    }
    const { data } = gameData as GameType;
    const game = toGameViewModel(gameData as GameType, { region, isMobile: true });

    const gameName = game.name ?? '';
    const backgroundImgUrl = game.backgroundUrl;
    const logoImgUrl = game.logoUrl;
    const nativeDescription = game.description;

    // Extract metadata for MetadataInfoSection (matching page version)
    const {
        genreUid,
        volatility,
        themes: unduplicatedThemes,
        features: unduplicatedFeatures,
        studioName
    } = getGameInfoModalMetadata(gameData as GameType);
    const gameMode = genreUid ? formatUid(genreUid, 'genre-') : null;
    const themes = deduplicateThemes(unduplicatedThemes);
    const features = deduplicateFeatures(unduplicatedFeatures);
    const isSiteWideJackpot = (data?.isSiteWideJackpot ?? false) && isSiteWideJackpotEnabled;

    const feedsCode = data?.feeds_code_mobile;
    const hasBadge = !!data?.label;
    const hasSocialTag = !!data?.badge_state || !!data?.unique_player_count;
    const hasFirstLine = hasBadge || hasSocialTag;

    return (
        <GameInfoModalWrapper show={isInQueue} onClose={closePopup} backgroundImgUrl={backgroundImgUrl}>
            <div
                className="game-info-page-metadata"
                data-testid="game-info-modal"
                data-t-module="game_info_modal"
                data-t-gamename={gameName}
                data-t-zonename="game_info_modal"
                data-t-gameuid={gameData.uid}
                data-t-activecount={
                    typeof data?.unique_player_count === 'number' ? String(data.unique_player_count) : ''
                }
                data-t-categoryuid="game_info_modal"
                data-t-dynamicordering={gameInfoPopup?.params?.isDynamicOrderingEnabled}
                data-t-isswj={isSiteWideJackpot}>
                {(backgroundImgUrl || logoImgUrl) && (
                    <div className="gip-image-container" data-testid="gip-image-container">
                        {backgroundImgUrl && (
                            <img
                                src={backgroundImgUrl}
                                alt={`${gameName} background`}
                                className="gip-background-image"
                            />
                        )}
                        {logoImgUrl && <img src={logoImgUrl} alt={`${gameName} logo`} className="gip-logo-image" />}
                        {hasFirstLine && (
                            <div className="gip-first-line">
                                {hasBadge && (
                                    <div className="gip-badge-wrapper">
                                        <BadgeFactoryClient feedsCode={feedsCode}>
                                            <TextBadge label={data!.label!} />
                                        </BadgeFactoryClient>
                                    </div>
                                )}
                                {hasSocialTag && (
                                    <div className="gip-social-tag">
                                        <SocialTagBadge
                                            state={data?.badge_state}
                                            uniquePlayersCount={data?.unique_player_count}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                        <SwjBorderOverlay isSiteWideJackpot={isSiteWideJackpot} swjLabel="default" />
                    </div>
                )}

                <div className="gip-details-section" data-testid="gip-details-section">
                    <h2 className="gip-title" data-testid="gip-title">
                        {gameName}
                    </h2>

                    <MetadataInfoSection
                        nativeDescription={nativeDescription}
                        gameMode={gameMode}
                        studioName={studioName}
                        volatility={volatility}
                        themes={themes}
                        features={features}
                        isSWJTickerAvailable={isSiteWideJackpot}
                    />

                    <div className="gip-action-bar" data-testid="gip-action-bar">
                        <FavoritesButton
                            uid={(gameData as GameType).uid}
                            variant={FAVORITES_VARIANT.OUTLINED}
                            isRenderRequired
                        />
                        <div className="gip-action-play">
                            <GameUrl launcherUrlProps={game.launcherUrlProps} data-testid="gip-play-button">
                                <span className="gip-play-button">{getText({ text: 'common.playNow' })}</span>
                            </GameUrl>
                        </div>
                    </div>
                </div>
            </div>
        </GameInfoModalWrapper>
    );
};
